import React from 'react';
import Select, { GroupBase, Props, StylesConfig, components, DropdownIndicatorProps } from 'react-select';
import ValidationMessage from '../validation/validationMessage';
import theme from '../../../shared/theme';
import { useValidationControl, ValidationControlProps } from 'src/shared/validation';

export interface ExtendedProps<T> extends ValidationControlProps<any> {
    onOptionFocus?: (e: T) => void;
    isError?: boolean;
    disabled?: boolean;
}

type LegacySelectOption = {
    value: string;
    label: string;
    isDisabled?: boolean;
};

export type AdvancedSelectProps<Option = any, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>> = Omit<
    Props<Option, IsMulti, Group>,
    'value' | 'onChange' | 'onBlur' | 'options'
> &
    ExtendedProps<Option> & {
        children?: React.ReactNode;
        value?: any;
        options?: Props<Option, IsMulti, Group>['options'];
        onChange?: (...args: any[]) => void;
        onBlur?: (...args: any[]) => void;
        multiple?: boolean;
        source?: { [key: string]: string };
    };

const ChevronDown = () => (
    <svg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M2.5 4.5L6 8L9.5 4.5' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
);

const CustomDropdownIndicator = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(props: DropdownIndicatorProps<Option, IsMulti, Group>) => (
    <components.DropdownIndicator {...props}>
        <ChevronDown />
    </components.DropdownIndicator>
);

function styles<Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>(
    isError?: boolean
): StylesConfig<Option, IsMulti, Group> {
    return {
        option: (provided, state) => {
            return {
                ...provided,
                fontSize: '13px',
                lineHeight: '16px',
                minHeight: 28,
                display: 'flex',
                alignItems: 'center',
                color: '#263238',
                fontWeight: 400,
                backgroundColor: state.isSelected ? '#f3f4f6' : state.isFocused ? '#f3f4f6' : '#ffffff',
                padding: '6px 10px',
                borderRadius: 4,
                cursor: state.isDisabled ? 'not-allowed' : 'pointer',
                ':active': {
                    backgroundColor: '#e5e7eb'
                }
            };
        },
        valueContainer: (provided) => {
            return {
                ...provided,
                fontSize: '13px',
                color: '#263238'
            };
        },
        menu: (provided) => {
            return {
                ...provided,
                // Keep the dropdown above sibling content such as the opacity slider (whose thumb/value sit at
                // z-index 2-3); at z-index 2 the menu tied with them and the slider value showed through.
                zIndex: 10,
                borderRadius: 6,
                border: '1px solid #e5e7eb',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)'
            };
        },
        menuList: (provided) => {
            return {
                ...provided,
                padding: 4
            };
        },
        control: (provided, state) => {
            return {
                ...provided,
                position: 'relative',
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: isError ? '#DE1C22' : state.isFocused ? theme.app.primary : '#e5e7eb',
                borderRadius: 6,
                boxShadow: isError ? '0 0 0 2px rgba(222, 28, 34, 0.2)' : 'none',
                minHeight: 32,
                height: 32,
                ':hover': {
                    borderColor: isError ? '#DE1C22' : theme.app.primary
                }
            };
        },
        indicatorsContainer: (provided) => {
            return {
                ...provided,
                height: 32
            };
        },
        indicatorSeparator: () => {
            return {
                display: 'none'
            };
        },
        dropdownIndicator: (provided) => {
            return {
                ...provided,
                color: '#263238',
                padding: '0 8px 0 0',
                ':hover': {
                    color: '#263238'
                }
            };
        },
        clearIndicator: (provided) => {
            return {
                ...provided,
                padding: '0 4px',
                color: '#9ca3af',
                ':hover': {
                    color: '#263238'
                }
            };
        },
        placeholder: (provided) => {
            return {
                ...provided,
                fontSize: '13px',
                color: '#9ca3af'
            };
        },
        groupHeading: (provided) => {
            return {
                ...provided,
                fontSize: '12px',
                fontWeight: 600,
                color: '#71717a',
                textTransform: 'none' as const,
                paddingLeft: 8,
                paddingBottom: 4,
                marginBottom: 0
            };
        },
        group: (provided) => {
            return {
                ...provided,
                paddingTop: 6,
                paddingBottom: 2
            };
        }
    };
}

export function AdvancedSelect<Option = any, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>(
    props: AdvancedSelectProps<Option, IsMulti, Group>
) {
    const {
        isError,
        validationKey,
        validate,
        validationMessage,
        message,
        validationLabel,
        validationValue,
        children,
        disabled,
        multiple,
        source,
        ...selectProps
    } = props;
    const legacyOptions = React.Children.toArray(children)
        .filter(React.isValidElement)
        .map((child) => {
            const props = child.props as { value?: string | number; children?: React.ReactNode; disabled?: boolean };
            return {
                value: props.value?.toString() ?? '',
                label: React.Children.toArray(props.children).join(''),
                isDisabled: props.disabled
            };
        });
    const hasLegacyOptions = legacyOptions.length > 0 && !selectProps.options;
    const normalizedOptions = hasLegacyOptions ? (legacyOptions as any) : selectProps.options;
    const normalizedValue = hasLegacyOptions
        ? ((legacyOptions.find((option) => option.value === selectProps.value?.toString()) ?? null) as Props<Option, IsMulti, Group>['value'])
        : selectProps.value;
    const normalizedOnChange = hasLegacyOptions
        ? (option: { value?: string } | null) => {
              (selectProps.onChange as ((value: string) => void) | undefined)?.(option?.value ?? '');
          }
        : selectProps.onChange;
    const normalizedOnBlur = hasLegacyOptions
        ? () => {
              (selectProps.onBlur as ((value: string) => void) | undefined)?.(selectProps.value?.toString() ?? '');
          }
        : selectProps.onBlur;
    const validation = useValidationControl<HTMLDivElement, any>(
        { validationKey, validate, validationMessage: validationMessage ?? message, validationLabel, validationValue },
        normalizedValue
    );
    const baseStyles = styles<Option, IsMulti, Group>(isError || validation.isValidationError);
    const mergedStyles: StylesConfig<Option, IsMulti, Group> = { ...baseStyles };

    if (selectProps.styles) {
        for (const key of Object.keys(selectProps.styles) as Array<keyof StylesConfig<Option, IsMulti, Group>>) {
            const baseFn = baseStyles[key] as ((...args: any[]) => any) | undefined;
            const overrideFn = selectProps.styles[key] as ((...args: any[]) => any) | undefined;
            if (baseFn && overrideFn) {
                (mergedStyles as any)[key] = (...args: any[]) => ({ ...baseFn(...args), ...overrideFn(...args) });
            } else if (overrideFn) {
                (mergedStyles as any)[key] = overrideFn;
            }
        }
    }

    return (
        <div ref={validation.setRef}>
            <Select<Option, IsMulti, Group>
                {...(selectProps as Props<Option, IsMulti, Group>)}
                value={normalizedValue}
                options={normalizedOptions as Props<Option, IsMulti, Group>['options']}
                onChange={normalizedOnChange as Props<Option, IsMulti, Group>['onChange']}
                onBlur={normalizedOnBlur as Props<Option, IsMulti, Group>['onBlur']}
                isDisabled={selectProps.isDisabled ?? disabled}
                styles={mergedStyles}
                components={{ DropdownIndicator: CustomDropdownIndicator, ...selectProps.components }}
            />
            {validation.validationKey && <ValidationMessage validationKey={validation.validationKey} deferUntilSubmit />}
        </div>
    );
}
